import { IssuedRosterTeamGroups } from "@/pages/roster-view/components/IssuedRosterTeamGroups";
import { useEffect, useMemo } from "react";
import type * as type from "@vTypes";
import { useQueryClient } from "@tanstack/react-query";
import type { RosterNurse, RosterResponse } from "@vTypes/snapshot-type.ts";
import type { InboundItem } from "@/hooks/roster-craete/useRosterSchedule";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import NurseRosterList from "@components/feature/rosterTable/row_NurseRosterList";
import { RosterTableBodyChrome } from "@components/feature/rosterTable/RosterTableBodyChrome";
import type { ShiftLookupMaps } from "@/utils/shift-util";
import type { ShiftCatalogShift } from "@/services/shift/shiftCatalog";
import { useVisibleShifts } from "@/hooks/shift/useVisibleShifts";
import useRosterConfig from "@/hooks/useRosterConfig";
import { useRosterDisplayShiftColumns } from "@/hooks/shift/useRosterDisplayShiftColumns";
import { useRosterFocusStore } from "@/store/rosterFocusStore";
import { nurseGroupSortKey } from "@utils/common-util.ts";
import useGroupMembers from "@/hooks/useGroupMembers";
import { MonthlyMembershipProvider } from "@/components/feature/nurse-membership/MonthlyMembershipContext";
import type { GroupMemberInMonth } from "@/api/nurses";
import type { RosterTableLayoutConfig } from "@/components/nurse/rosterColumnHoverLayout";

export type RosterNurseView = RosterNurse & {
  /** 발행 뷰: `Roster_view`에서 루트 `assignments` 병합 */
  assignments?: InboundItem[];
  preceptor?: {
    color: string;
    ee: string[];
    group: number;
    groupIndex?: number;
    or: string[];
    role: string;
  } | null;
  role?: string | null;
  schedule_ids?: (number | null)[] | null;
  off?: number;
  grade?: number | null;
  age?: number | string | null;
  level?: string | null;
};

const noopModDay = () => {};

/** `NurseRosterList` 발행 조회 행은 `schedule: string[]` — 스냅샷은 `{ code, color }[]` */
function mapIssuedScheduleToCodes(
  schedule: RosterNurseView["schedule"] | null | undefined
): string[] | null {
  if (schedule == null) return null;
  return schedule.map((cell) => (typeof cell === "string" ? cell : cell.code));
}

export type TableBodyViewProps = {
  teamView?: boolean;
  layoutConfig?: RosterTableLayoutConfig;
  nurses: RosterNurseView[] | null;
  shiftInfo: type.availableShifts[];
  shiftLookupMaps: ShiftLookupMaps<ShiftCatalogShift>;
  year: number;
  month: number;
  defaultShiftCode: type.availableShifts[];
  showPerShiftStats?: boolean;
  /** `useSnapShot` 쿼리키와 맞춤 — 발행 조회 전체 탭 병동 전환 시 */
  issuedSnapshotGroupId?: string;
  /** 현재 발행 조회 중인 병동 group_id — inbound outbound 분류 기준 */
  currentGroupId?: string | null;
};

export function IssuedRosterTableEmpty({
  year,
  month,
}: {
  year: number;
  month: number;
}) {
  return (
    <div className="py-10 flex flex-col items-center justify-center gap-1">
      <div className=" font-semibold  text-center text-2xl text-foreground">
        {year}년 {month}월 발행된 근무표가 없습니다
      </div>
      <p className="text-base text-muted-foreground">
        근무표 발행은 관리자에게 문의해주세요.
      </p>
    </div>
  );
}

function TableBodyView({
  teamView = false,
  layoutConfig,
  shiftInfo,
  shiftLookupMaps,
  year,
  nurses,
  month,
  defaultShiftCode,
  showPerShiftStats = true,
  issuedSnapshotGroupId = "",
  currentGroupId = null,
}: TableBodyViewProps) {
  const daysInMonth = useMemo(() => {
    if (!year || month < 1 || month > 12) return 0;
    return new Date(year, month, 0).getDate();
  }, [year, month]);

  const queryClient = useQueryClient();
  const queries = queryClient.getQueriesData<RosterResponse>({
    queryKey: QUERY_KEYS.roster.issuedRosterSnapshot(
      year,
      month,
      issuedSnapshotGroupId
    ),
  });
  void queries;

  const { data: configData } = useRosterConfig();
  const isUseMid = configData?.use_mid;

  const baselineVisibleShiftIds = useMemo(() => {
    const order = ["D", "E", "N", "M", "O"] as const;
    const byDefault = new Map<string, string>();
    (defaultShiftCode ?? []).forEach((shift: type.availableShifts) => {
      const ds = shift?.default_shift;
      if (!ds || byDefault.has(ds)) return;
      byDefault.set(ds, shift.shift_id);
    });

    return order
      .filter((code) => !(code === "M" && isUseMid === false))
      .map((code) => byDefault.get(code))
      .filter((id): id is string => !!id);
  }, [defaultShiftCode, isUseMid]);

  const {
    columnShiftIds: statsColumnShiftIds,
    displayShifts: statsDisplayShifts,
  } = useRosterDisplayShiftColumns(
    nurses as
      | {
          schedule?: (string | null | { code: string } | null)[] | null;
          schedule_ids?: (number | null)[] | null;
        }[]
      | null,
    shiftInfo,
    isUseMid,
    { includeBaselineMainTemplateColumns: true }
  );

  const autoVisibleShifts = useVisibleShifts(nurses ?? [], shiftInfo ?? []);
  const visibleShifts =
    baselineVisibleShiftIds.length > 0
      ? baselineVisibleShiftIds
      : autoVisibleShifts;

  useEffect(() => {
    const clickWindow = (e: Event) => {
      const clickTarget = e.target as HTMLElement;
      if (!clickTarget.closest(".preceptorMark")) {
        document
          .querySelectorAll(".preceptorGroup")
          .forEach((tr) => tr.classList.remove("click"));
      }
    };
    window.addEventListener("click", clickWindow);
    return () => {
      window.removeEventListener("click", clickWindow);
    };
  }, []);

  const clearSelectedFocus = useRosterFocusStore(
    (state) => state.clearSelectedFocus
  );

  /** `TableBody_base` 와 동일 — 헤더·셀 밖 클릭 시 열 선택 해제 */
  useEffect(() => {
    const clearWhenOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (
        !target?.closest("[data-roster-shift-cell]") &&
        !target?.closest("[data-roster-header-day]")
      ) {
        clearSelectedFocus();
      }
    };
    window.addEventListener("click", clearWhenOutside, true);
    return () => window.removeEventListener("click", clearWhenOutside, true);
  }, [clearSelectedFocus]);

  useEffect(() => {
    clearSelectedFocus();
  }, [year, month, clearSelectedFocus]);

  useEffect(() => {
    return () => {
      clearSelectedFocus();
    };
  }, [clearSelectedFocus]);

  const { data: monthMembers } = useGroupMembers(currentGroupId, year, month, {
    enabled: Boolean(currentGroupId && year && month),
  });
  const memberStatusMap = useMemo<Map<string, GroupMemberInMonth> | undefined>(
    () => {
      if (!monthMembers) return undefined;
      return new Map(
        monthMembers.members.map((member) => [member.nurse_id, member] as const)
      );
    },
    [monthMembers]
  );

  if (!nurses?.length) {
    return <IssuedRosterTableEmpty year={year} month={month} />;
  }

  const rowIndexById = new Map(nurses.map((nurse, index) => [nurse.nurse_id, index]));
  const renderRows = (rows: RosterNurseView[]) => rows.map((nurse, index) => {
    const nextNurse = rows[index + 1];
    const roleGroupBottomBorder =
      nextNurse != null &&
      nurseGroupSortKey(nurse) !== nurseGroupSortKey(nextNurse);

    const rowNurse = {
      counts: nurse.counts ?? null,
      experience: nurse.experience,
      id: String(nurse.nurse_id),
      name: nurse.name,
      age: nurse.age,
      grade: nurse.grade,
      preceptor: nurse.preceptor ?? null,
      schedule: mapIssuedScheduleToCodes(nurse.schedule),
      off: nurse.off ?? 0,
      level: String(nurse.level ?? ""),
      role: nurse.role ?? "",
      schedule_ids: nurse.schedule_ids,
      is_inbound: nurse.is_inbound,
      inbound: nurse.inbound,
      assignments: (nurse as RosterNurseView).assignments,
    };

    return (
      <NurseRosterList
        layoutConfig={layoutConfig}
        key={rowNurse.id}
        issuedRosterView
        isEditMode={false}
        nursesIndex={rowIndexById.get(nurse.nurse_id) ?? index}
        displayRowNumber={index + 1}
        nurse={rowNurse}
        isSubmitted={false}
        availableShifts={shiftInfo}
        shiftLookupMaps={shiftLookupMaps}
        modDayShift={noopModDay}
        selectedShift={null}
        statsColumnShiftIds={statsColumnShiftIds}
        statsDisplayShifts={statsDisplayShifts}
        prevTailData={undefined}
        cellViolationMap={undefined}
        scheduleYear={year}
        scheduleMonth={month}
        schedulePayloadYear={year}
        schedulePayloadMonth={month}
        showPerShiftStats={showPerShiftStats}
        visibleShifts={visibleShifts}
        roleGroupBottomBorder={roleGroupBottomBorder}
        currentGroupId={currentGroupId}
      />
    );
  });

  return (
    <MonthlyMembershipProvider
      memberStatusMap={memberStatusMap}
      selectedGroupId={currentGroupId}
    >
      <RosterTableBodyChrome
        unframed={teamView}
        layoutConfig={layoutConfig}
        daysInMonth={daysInMonth}
        hasPrevTail={false}
        compactVerticalPadding={false}
      >
        {teamView ? (
          <IssuedRosterTeamGroups nurses={nurses} groupId={currentGroupId} year={year} month={month} layoutConfig={layoutConfig}>
            {renderRows}
          </IssuedRosterTeamGroups>
        ) : renderRows(nurses)}
      </RosterTableBodyChrome>
    </MonthlyMembershipProvider>
  );
}

export default TableBodyView;
